import React from 'react';
import { PinValidation } from '@gps-hunt/shared';

interface GpsSignalIndicatorProps {
  accuracy: number | null;
  isLoading?: boolean;
  isWatching?: boolean;
  className?: string;
}

type SignalLevel = 'excellent' | 'good' | 'fair' | 'poor' | 'none';

/**
 * Get signal level based on accuracy
 * 10 bars total:
 * - ≤5m: 10 bars (excellent)
 * - ≤10m: 8 bars (good)
 * - ≤15m: 6 bars (fair)
 * - ≤20m: 4 bars (fair)
 * - >20m: 2 bars (poor)
 * - null: 0 bars (none)
 */
function getSignalLevel(accuracy: number | null): { level: SignalLevel; bars: number } {
  if (accuracy === null) return { level: 'none', bars: 0 };
  if (accuracy <= 5) return { level: 'excellent', bars: 10 };
  if (accuracy <= 10) return { level: 'good', bars: 8 };
  if (accuracy <= 15) return { level: 'fair', bars: 6 };
  if (accuracy <= PinValidation.accuracy.max) return { level: 'fair', bars: 4 };
  return { level: 'poor', bars: 2 };
}

/**
 * Get bar color classes based on position and signal level
 */
function getBarColor(index: number, bars: number): string {
  if (index >= bars) return 'bg-gray-700'; // Inactive bar
  
  // Color gradient: first bars are green, middle are yellow, last are red
  // Based on position in the 10-bar array
  if (index < 4) return 'bg-red-500';
  if (index < 7) return 'bg-yellow-500';
  return 'bg-green-500';
}

/**
 * GPS Signal strength indicator with colored bars
 * Displays signal strength as 10 vertical bars plus accuracy text
 */
export const GpsSignalIndicator: React.FC<GpsSignalIndicatorProps> = ({
  accuracy,
  isLoading = false,
  isWatching = false,
  className = '',
}) => {
  const { level, bars } = getSignalLevel(accuracy);
  const totalBars = 10;

  // Status text
  const getStatusText = () => {
    if (isLoading) return 'Locating...';
    if (!isWatching) return 'GPS Off';
    if (accuracy === null) return 'No Signal';
    return 'Connected';
  };

  const statusColor = () => {
    if (isLoading || !isWatching || accuracy === null) return 'text-gray-400';
    if (level === 'excellent' || level === 'good') return 'text-green-500';
    if (level === 'fair') return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Signal bars */}
      <div className="flex items-end gap-[2px] h-4">
        {Array.from({ length: totalBars }).map((_, i) => (
          <div
            key={i}
            className={`w-[3px] rounded-sm transition-all duration-200 ${getBarColor(i, bars)}`}
            style={{
              height: `${40 + (i * 6)}%`,
              opacity: isLoading ? 0.5 : 1,
            }}
          />
        ))}
      </div>

      {/* Status text and accuracy */}
      <div className="flex flex-col items-start leading-none">
        <span className={`text-[10px] font-medium ${statusColor()}`}>
          {getStatusText()}
        </span>
        {accuracy !== null && isWatching && !isLoading && (
          <span className="text-[10px] text-gray-400 font-mono">
            ±{accuracy.toFixed(0)}m
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Compact horizontal bar indicator (like cell signal)
 */
export const GpsSignalBars: React.FC<Pick<GpsSignalIndicatorProps, 'accuracy' | 'isLoading'>> = ({
  accuracy,
  isLoading = false,
}) => {
  const { bars } = getSignalLevel(accuracy);
  const totalBars = 10;

  return (
    <div className="flex items-end gap-[1px] h-3">
      {Array.from({ length: totalBars }).map((_, i) => (
        <div
          key={i}
          className={`w-[2px] rounded-[1px] transition-all duration-200 ${getBarColor(i, bars)}`}
          style={{
            height: `${30 + (i * 7)}%`,
            opacity: isLoading ? 0.5 : 1,
          }}
        />
      ))}
    </div>
  );
};

/**
 * Full-width horizontal GPS signal strip for header bottom edge
 * Shows colored segments based on signal strength
 */
export const GpsSignalStrip: React.FC<Pick<GpsSignalIndicatorProps, 'accuracy' | 'isLoading' | 'isWatching'>> = ({
  accuracy,
  isLoading = false,
  isWatching = false,
}) => {
  const { bars } = getSignalLevel(accuracy);
  const totalBars = 10;

  // Don't show if not watching GPS
  if (!isWatching) return null;

  return (
    <div 
      className="flex w-full h-[3px]"
      style={{ opacity: isLoading ? 0.6 : 1 }}
    >
      {Array.from({ length: totalBars }).map((_, i) => (
        <div
          key={i}
          className={`flex-1 transition-all duration-300 ${getBarColor(i, bars)}`}
        />
      ))}
    </div>
  );
};

export default GpsSignalIndicator;
